<template>
  <div class="upload-material-component">
    <upload-component
      accepted="pdf"
      :uploadPercentage="uploadProgress"
      :activeFileName="upload.originalFileName"
      :isLoading="isUploading"
      :isInvalid="isUploadError"
      :isFinished="isUploadSuccess"
      :maxFilesize="maxFilesize"
      @filesAdded="fileAddded($event.files)"
      @reset="resetUpload"
    >
      <img
        slot="image"
        :src="require(`@/assets/images/file-upload-pdf-doc.svg`)"
      />
      <p slot="headline" class="headline" v-if="!isMobileView">
        {{ $t('W2P_RECIPIENTS_DRAG_FILE') }}
        <button class="browse-button">
          {{ $t('W2P_RECIPIENTS_BROWSE') }}
        </button>
      </p>
      <button slot="headline" class="browse-button" v-else>
        {{ $t('W2P_RECIPIENTS_BROWSE_FOR_A_FILE') }}
      </button>
      <p slot="description" class="description">
        {{
          $t('W2P_UPLOAD_MATERIAL_INSTRUCTION', {
            pageFormat: getPageFormat(cart.productDelivery.format.name),
            maxPage: cart.productDelivery.page_count.max,
            maxSize: `${maxFilesize}MB`
          })
        }}
      </p>
    </upload-component>

    <div class="result-section">
      <div v-if="isUploadSuccess" class="upload-success">
        {{ $t('W2P_UPLOAD_MATERIAL_SUCCESS_MSG') }}
      </div>
      <div v-if="isUploadError" class="upload-error">
        <div class="title">
          <pn-icon
            symbol="alert-exclamation-circle"
            size="medium"
            color="warning"
            solid
          ></pn-icon>
          <p
            v-html="
              $t('W2P_UPLOAD_MATERIAL_ERROR_MSG', {
                count: uploadErrors.length,
                fileName: upload.originalFileName
              })
            "
          ></p>
        </div>

        <div class="messages">
          <p
            class="error-message"
            v-for="(error, i) in uploadErrors"
            :key="i"
            v-html="getErrorMessage(error)"
          ></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as utils from '@/helpers/utilHelper.js'
import * as uploadMaterialHelper from '@/helpers/uploadMaterialHelper'
import { mapMutations } from 'vuex'
import UploadComponent from '@/components/common/UploadComponent'

export default {
  name: 'UploadMaterialComponent',
  props: {
    service: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isUploading: false,
      uploadErrors: [],
      uploadSteps: 2,
      uploadCurrentStep: 0,
      maxFilesize: 50, // in MB
      file: {},
      isMobileView: this.checkViewWidth(window.innerWidth)
    }
  },
  components: {
    UploadComponent
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize)
  },
  computed: {
    state() {
      return this.$store.state[this.service]
    },
    upload() {
      return this.state.upload
    },
    cart() {
      return this.$store.state.cart
    },
    uploadProgress() {
      return utils.calculateProgress(this.uploadSteps, this.uploadCurrentStep)
    },
    isUploadSuccess() {
      return this.uploadSteps === this.uploadCurrentStep && !this.isUploadError
    },
    isUploadError() {
      return this.uploadErrors.length > 0
    }
  },
  methods: {
    ...mapMutations({
      saveMaterial(commit, payload) {
        return commit(this.service + '/upload/SAVE_MATERIAL', payload)
      },
      setMaterial(commit, payload) {
        return commit(this.service + '/upload/SET_MATERIAL', payload)
      },
      resetMaterial(commit) {
        return commit(this.service + '/upload/RESET_MATERIAL')
      }
    }),
    async uploadSuccess(file) {
      try {
        const meta = {
          service: this.cart.serviceType
        }
        const response = await uploadMaterialHelper.uploadMaterial(
          this.service,
          file,
          'validation',
          'material.pdf',
          meta,
          () => {
            this.uploadCurrentStep++
          }
        )

        this.saveMaterial({
          ...this.upload,
          ...response
        })

        if (!response.valid) {
          const errKeys = this.$store.getters[
            this.service + '/upload/getResultErrors'
          ]

          this.uploadErrors = this.uploadErrors.concat(
            errKeys.map(err => err.label)
          )
        }

        this.isUploading = false

        this.$emit('materialUploaded')
      } catch (error) {
        this.uploadError()
      }
    },
    uploadError(file) {
      this.isUploading = false

      if (file && utils.bytesToMb(file.size) > this.maxFilesize) {
        this.uploadErrors.push({
          key: 'W2P_UPLOAD_MATERIAL_INVALID_FILE_SIZE',
          options: {
            size: `${this.maxFilesize}MB`
          }
        })
      } else {
        this.uploadErrors.push('W2P_UPLOAD_MATERIAL_INVALID_FILE')
      }
    },
    fileAddded(res) {
      const file = res[0].file
      this.resetUpload()
      this.setMaterial({
        key: 'originalFileName',
        value: file.name
      })

      if (!res[0].validated) {
        this.uploadError(file)
        return
      }

      this.isUploading = true

      this.uploadSuccess(file)
    },
    clientUploadProgress(totaluploadprogress) {
      if (totaluploadprogress === 100) return
      this.uploadCurrentStep = totaluploadprogress / 100
    },
    resetUpload() {
      this.isUploading = false
      this.uploadErrors = []
      this.uploadCurrentStep = 0
      this.resetMaterial()
    },
    removeFile() {
      this.resetUpload()
    },
    getErrorMessage(err) {
      if (typeof err === 'string') {
        return this.$t(err)
      }

      if (err.options.hasOwnProperty('pageFormat')) {
        err.options.pageFormat = this.$t(err.options.pageFormat)
      }

      return this.$t(err.key, { ...err.options })
    },
    getPageFormat(formatName) {
      return this.$t(uploadMaterialHelper.getPageFormat(formatName))
    },
    onWindowResize(e) {
      const width = e.target.innerWidth

      this.isMobileView = this.checkViewWidth(width)
    },
    checkViewWidth(width) {
      return width <= 768
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-material-component {
  font-size: 1.6rem;
  color: $gray700;

  .browse-button {
    padding: 0;
    border: none;
    background: none;
    font-size: 1em;
    color: $blue900;
  }

  p {
    color: $gray700;
    font-weight: 300;

    &.headline {
      margin-top: 1em;
    }
    &.description {
      font-size: 0.75em;
      margin-top: 0.5em;
    }
  }

  .result-section {
    font-size: 1em;

    .upload-success {
      margin-top: 0.5em;
      font-size: 0.875em;
      color: $green900;
    }

    .upload-error {
      margin-top: 1.5em;

      .title {
        border: 0.063em solid $warning;
        color: $black;
        display: flex;
        padding: 1.375em 2em;

        pn-icon {
          margin: auto;
          margin-right: 1em;
        }
      }

      .messages {
        border: 1px solid $gray50;

        .error-message {
          display: flex;
          padding: 1.375em 2em;
          color: $black;

          &:nth-child(odd) {
            background-color: $coral50;
          }

          &:nth-child(even) {
            background-color: $white;
          }
        }
      }
    }
  }
}
</style>
